import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Paid as PaidIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { 
  getInvoices, 
  deleteInvoice, 
  updateInvoiceStatus, 
  getInvoiceStats, 
  sendInvoice, 
  markAsPaid 
} from '../../services/api';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [statusData, setStatusData] = useState({ status: '', payment_status: '' });
  const [filters, setFilters] = useState({
    status: '',
    payment_status: '',
    start_date: '',
    end_date: ''
  });
  
  const navigate = useNavigate();

  const statusColors = {
    draft: 'default',
    sent: 'info',
    paid: 'success',
    overdue: 'error',
    cancelled: 'warning'
  };

  const paymentStatusColors = {
    pending: 'warning',
    partial: 'info',
    paid: 'success'
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.payment_status) queryParams.append('payment_status', filters.payment_status);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      
      const queryString = queryParams.toString();
      const [invoicesResponse, statsResponse] = await Promise.all([
        getInvoices(queryString ? `?${queryString}` : ''),
        getInvoiceStats()
      ]);
      
      if (invoicesResponse.data.success) {
        setInvoices(invoicesResponse.data.data);
      }
      
      if (statsResponse.data.success) {
        setStats(statsResponse.data.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch invoices');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleDeleteClick = (invoice) => {
    setSelectedInvoice(invoice);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteInvoice(selectedInvoice.id);
      if (response.data.success) {
        await fetchInvoices();
        setDeleteDialogOpen(false);
        setSelectedInvoice(null);
      }
    } catch (err) {
      console.error('Error deleting invoice:', err);
    }
  };

  const handleStatusClick = (invoice) => {
    setSelectedInvoice(invoice);
    setStatusData({
      status: invoice.status,
      payment_status: invoice.payment_status
    });
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    try {
      const response = await updateInvoiceStatus(selectedInvoice.id, statusData);
      if (response.data.success) {
        await fetchInvoices();
        setStatusDialogOpen(false);
        setSelectedInvoice(null);
        setStatusData({ status: '', payment_status: '' });
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleSendClick = (invoice) => {
    setSelectedInvoice(invoice);
    setSendDialogOpen(true);
  };

  const handleSendConfirm = async () => {
    try {
      const response = await sendInvoice(selectedInvoice.id);
      if (response.data.success) {
        await fetchInvoices();
        setSendDialogOpen(false);
        setSelectedInvoice(null);
      }
    } catch (err) {
      console.error('Error sending invoice:', err);
    }
  };

  const handlePaidClick = (invoice) => {
    setSelectedInvoice(invoice);
    setPaidDialogOpen(true);
  };

  const handlePaidConfirm = async () => {
    try {
      const response = await markAsPaid(selectedInvoice.id);
      if (response.data.success) {
        await fetchInvoices();
        setPaidDialogOpen(false);
        setSelectedInvoice(null);
      }
    } catch (err) {
      console.error('Error marking as paid:', err);
    }
  };

  const handleCreateInvoice = () => {
    navigate('/invoices/new');
  };

  const handleViewInvoice = (id) => {
    navigate(`/invoices/${id}`);
  };

  const handleEditInvoice = (id) => {
    navigate(`/invoices/edit/${id}`);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchInvoices();
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      payment_status: '',
      start_date: '',
      end_date: ''
    });
    fetchInvoices();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-LK');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    return due < today;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <ReceiptIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Invoice Management
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchInvoices}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateInvoice}
          >
            New Invoice
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Invoices
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalInvoices || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalRevenue ? formatCurrency(stats.totalRevenue) : 'LKR 0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Overdue Invoices
              </Typography>
              <Typography variant="h5" component="div">
                {stats.overdueInvoices || 0}
              </Typography>
              <Typography variant="caption" color="error">
                {stats.overdueAmount ? formatCurrency(stats.overdueAmount) : 'LKR 0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Today's Invoices
              </Typography>
              <Typography variant="h5" component="div">
                {stats.todayInvoices ? formatCurrency(stats.todayInvoices) : 'LKR 0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                label="Payment Status"
                name="payment_status"
                value={filters.payment_status}
                onChange={handleFilterChange}
              >
                <MenuItem value="">All Payment Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                fullWidth
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                fullWidth
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Invoices Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Invoice Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => {
                  const overdue = isOverdue(invoice.due_date);
                  
                  return (
                    <TableRow 
                      key={invoice.id} 
                      hover
                      sx={{
                        backgroundColor: overdue && invoice.status !== 'paid' ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {invoice.invoice_number}
                        </Typography>
                        {overdue && invoice.status !== 'paid' && (
                          <Chip
                            label="Overdue"
                            size="small"
                            color="error"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {invoice.Customer?.name || 'N/A'}
                      </TableCell>
                      <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {formatDate(invoice.due_date)}
                          {overdue && invoice.status !== 'paid' && (
                            <TrendingUpIcon color="error" fontSize="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{formatCurrency(invoice.total_amount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status}
                          color={statusColors[invoice.status]}
                          size="small"
                          onClick={() => handleStatusClick(invoice)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.payment_status}
                          color={paymentStatusColors[invoice.payment_status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewInvoice(invoice.id)}
                          title="View"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditInvoice(invoice.id)}
                          title="Edit"
                          disabled={invoice.status === 'paid' || invoice.status === 'sent'}
                        >
                          <EditIcon />
                        </IconButton>
                        {invoice.status === 'draft' && (
                          <IconButton
                            size="small"
                            onClick={() => handleSendClick(invoice)}
                            title="Send"
                          >
                            <SendIcon />
                          </IconButton>
                        )}
                        {invoice.payment_status !== 'paid' && (
                          <IconButton
                            size="small"
                            onClick={() => handlePaidClick(invoice)}
                            title="Mark as Paid"
                          >
                            <PaidIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(invoice)}
                          title="Delete"
                          disabled={invoice.status === 'paid' || invoice.status === 'sent'}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete invoice {selectedInvoice?.invoice_number}?
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Update Invoice Status</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Invoice Status"
                value={statusData.status}
                onChange={(e) => setStatusData(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Payment Status"
                value={statusData.payment_status}
                onChange={(e) => setStatusData(prev => ({ ...prev, payment_status: e.target.value }))}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Invoice Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)}>
        <DialogTitle>Send Invoice</DialogTitle>
        <DialogContent>
          Are you sure you want to send invoice {selectedInvoice?.invoice_number} to the customer?
          This will mark the invoice as "Sent" and cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSendConfirm} variant="contained" color="primary">
            Send Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <Dialog open={paidDialogOpen} onClose={() => setPaidDialogOpen(false)}>
        <DialogTitle>Mark as Paid</DialogTitle>
        <DialogContent>
          Are you sure you want to mark invoice {selectedInvoice?.invoice_number} as paid?
          This will update both status and payment status to "Paid".
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaidDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePaidConfirm} variant="contained" color="success">
            Mark as Paid
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Invoices;